import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Modal, Popconfirm, Select, Space, Spin, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import sshKeyService from '../../../services/sshKeyService';
import { ok, runAction, errMsg } from './utils';

const { Text } = Typography;

const StateTag = ({ state, pending }) => {
    const { t } = useTranslation();
    if (state === 'applying') return <Tag icon={<SyncOutlined spin />} color="processing">{t('Applying')}</Tag>;
    if (state === 'restart' || pending) return <Tag color="warning">{t('Takes effect after restart')}</Tag>;
    if (state === 'failed') return <Tag color="error">{t('Apply failed')}</Tag>;
    return null;
};

/**
 * SSH keys of one VM. User: bind / unbind saved keys, key-only switch.
 * Admin: fingerprints + key-only state, clear all / turn key-only off.
 */
const SshKeysTab = ({ id, api, mode, active, disabled }) => {
    const { t } = useTranslation();
    const isAdmin = mode === 'admin';
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [bindOpen, setBindOpen] = useState(false);
    const [saved, setSaved] = useState([]);
    const [pick, setPick] = useState([]);

    const load = useCallback(async () => {
        const r = await api.getVmSshKeys(id);
        if (ok(r)) { setData(r.data); setError(null); } else setError(errMsg(r, t('Failed to load')));
    }, [api, id, t]);

    useEffect(() => { if (active) load(); }, [active, load]);

    // poll while the change is being applied in the guest
    useEffect(() => {
        if (!active || data?.state !== 'applying') return undefined;
        const timer = setInterval(load, 3000);
        return () => clearInterval(timer);
    }, [active, data?.state, load]);

    const act = async (fn, success) => {
        setBusy(true);
        const r = await runAction(fn, success, t('Operation failed'));
        setBusy(false);
        if (r?.data && typeof r.data === 'object' && 'keys' in r.data) setData(r.data);
        else if (r) load();
        return r;
    };

    const openBind = async () => {
        setPick([]);
        setBindOpen(true);
        const r = await sshKeyService.list();
        if (ok(r)) setSaved(r.data?.items || []);
    };

    const submitBind = async () => {
        const r = await act(() => api.bindVmSshKeys(id, pick), t('Submitted'));
        if (r) setBindOpen(false);
    };

    if (!data) {
        if (error) return <Alert type="error" showIcon message={error} />;
        return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
    }
    if (!data.supported) {
        return <Empty description={data.reason === 'windows' ? t('SSH keys are not supported on Windows instances') : t('This image does not support SSH keys')} />;
    }

    const keys = data.keys || [];
    const boundKeyIds = new Set(keys.map((k) => k.key_id).filter(Boolean));
    const applying = data.state === 'applying';
    const locked = disabled || busy || applying;

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            render: (v, r) => (
                <Space size={4}>
                    <span>{v || '-'}</span>
                    {!r.saved && <Tooltip title={t('Not in My Keys')}><Tag style={{ marginInlineEnd: 0 }}>{t('Unsaved')}</Tag></Tooltip>}
                </Space>
            ),
        },
        { title: t('Fingerprint'), dataIndex: 'fingerprint', render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
        { title: t('Type'), dataIndex: 'type', width: 160, render: (v) => String(v || '').replace(/@openssh\.com$/, '') },
        ...(!isAdmin ? [{
            title: t('Actions'),
            width: 80,
            render: (_, r) => (
                <Popconfirm
                    title={t('Unbind this key?')}
                    onConfirm={() => act(() => api.unbindVmSshKeys(id, [r.id]), t('Submitted'))}
                    okText={t('Unbind')}
                    okButtonProps={{ danger: true }}
                    cancelText={t('Cancel')}
                    disabled={locked || (data.key_only && keys.length <= 1)}
                >
                    <Button type="link" size="small" danger disabled={locked || (data.key_only && keys.length <= 1)}>{t('Unbind')}</Button>
                </Popconfirm>
            ),
        }] : []),
    ];

    const keyOnlySwitch = (
        <Space size={8}>
            <Text>{t('Disable password login')}</Text>
            <Tooltip title={t('SSH accepts keys only; the VNC console still accepts the password')}>
                <Switch
                    size="small"
                    checked={!!data.key_only}
                    loading={busy}
                    disabled={isAdmin ? (locked || !data.key_only) : (locked || (!data.key_only && keys.length === 0))}
                    onChange={(v) => {
                        if (isAdmin) {
                            Modal.confirm({
                                title: t('Turn off key-only login?'),
                                okText: t('OK'),
                                cancelText: t('Cancel'),
                                onOk: () => act(() => api.setVmSshKeyOnly(id, false), t('Submitted')),
                            });
                        } else {
                            act(() => api.setVmSshKeyOnly(id, v), t('Submitted'));
                        }
                    }}
                />
            </Tooltip>
        </Space>
    );

    return (
        <Card
            size="small"
            title={(
                <Space size={8} wrap>
                    <span>{t('SSH Keys')}</span>
                    {data.login_user && <Text type="secondary" style={{ fontWeight: 'normal' }}>{data.login_user}</Text>}
                    <StateTag state={data.state} pending={data.pending_restart && data.state === 'restart'} />
                </Space>
            )}
            extra={(
                <Space size={12} wrap>
                    {keyOnlySwitch}
                    {isAdmin ? (
                        <Popconfirm
                            title={t('Clear all keys?')}
                            description={t('Key-only login is turned off as well')}
                            onConfirm={() => act(() => api.clearVmSshKeys(id), t('Submitted'))}
                            okText={t('Clear')}
                            okButtonProps={{ danger: true }}
                            cancelText={t('Cancel')}
                            disabled={locked || (keys.length === 0 && !data.key_only)}
                        >
                            <Button size="small" danger disabled={locked || (keys.length === 0 && !data.key_only)}>{t('Clear all keys')}</Button>
                        </Popconfirm>
                    ) : (
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={locked || (data.max && keys.length >= data.max)}
                            onClick={openBind}
                        >
                            {t('Bind')}
                        </Button>
                    )}
                </Space>
            )}
        >
            {data.state === 'restart' && !isAdmin && (
                <Alert style={{ marginBottom: 12 }} type="info" showIcon message={t('Guest agent unavailable; takes effect after restart')} />
            )}
            <Table rowKey="id" size="small" dataSource={keys} columns={columns} pagination={false} locale={{ emptyText: t('No keys bound') }} />

            <Modal
                title={t('Bind keys')}
                open={bindOpen}
                onCancel={() => setBindOpen(false)}
                onOk={submitBind}
                okButtonProps={{ disabled: pick.length === 0 }}
                confirmLoading={busy}
                destroyOnClose
            >
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    value={pick}
                    onChange={setPick}
                    placeholder={t('Select my keys')}
                    optionFilterProp="label"
                    notFoundContent={t('No keys')}
                    options={saved.filter((k) => !boundKeyIds.has(k.id)).map((k) => ({ value: k.id, label: `${k.name} · ${k.fingerprint}` }))}
                />
            </Modal>
        </Card>
    );
};

export default SshKeysTab;
